import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { todayLocalDate } from "@/lib/date";
import { formatDate, formatMoney, formatPercent } from "@bookboost/shared/format";

interface UserRow {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  hubspot_owner_id: string | null;
  salary_currency: string | null;
}
interface Structure {
  id: string;
  user_id: string;
  structure_type: "flat" | "split_equity";
  base_commission_rate: number;
  equity_split_rate: number | null;
  cash_split_rate: number | null;
  equity_annual_cap: number | null;
  cap_currency: string | null;
  equity_grant_id: string | null;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}
interface Grant {
  id: string;
  user_id: string;
  granted_on: string;
  superseded_on: string | null;
  total_shares: number;
  annual_share_cap: number;
  share_value_amount: number;
  share_value_currency: string;
  vehicle: string | null;
  entity_jurisdiction: string | null;
  grant_horizon_years: number;
  unearned_rollover: boolean;
  notes: string | null;
}
interface DetailResponse {
  user: UserRow;
  structures: Structure[];
  grants: Grant[];
}

export const Route = createFileRoute("/admin/reps_/$id/structures")({
  component: StructuresPage,
});

function StructuresPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reps-detail", id],
    queryFn: () => api.get<DetailResponse>(`/api/admin/reps-detail/${id}`),
  });

  const createStructure = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/api/admin/reps-detail/${id}/structures`, body),
    onSuccess: () => {
      toast.ok("Structure saved.");
      qc.invalidateQueries({ queryKey: ["admin", "reps-detail", id] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't save."),
  });

  const deleteStructure = useMutation({
    mutationFn: (structureId: string) => api.delete(`/api/admin/reps-detail/${id}/structures/${structureId}`),
    onSuccess: () => {
      toast.ok("Deleted.");
      qc.invalidateQueries({ queryKey: ["admin", "reps-detail", id] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't delete."),
  });

  const createGrant = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/api/admin/reps-detail/${id}/grants`, body),
    onSuccess: () => {
      toast.ok("Grant saved.");
      qc.invalidateQueries({ queryKey: ["admin", "reps-detail", id] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't save grant."),
  });

  const deleteGrant = useMutation({
    mutationFn: (grantId: string) => api.delete(`/api/admin/reps-detail/${id}/grants/${grantId}`),
    onSuccess: () => {
      toast.ok("Grant deleted.");
      qc.invalidateQueries({ queryKey: ["admin", "reps-detail", id] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't delete grant."),
  });

  if (isLoading) return <p className="lede">Loading…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Rep not found</h2>
        <p>{error instanceof Error ? error.message : "—"}</p>
        <p>
          <Link to="/admin/reps">← back to reps</Link>
        </p>
      </Panel>
    );
  }

  const openStructure = data.structures.find((s) => !s.effective_to);
  const openGrant = data.grants.find((g) => !g.superseded_on);

  return (
    <>
      <p>
        <Link to="/admin/reps">← back to reps</Link>
      </p>

      <h1>{data.user.full_name ?? data.user.email}</h1>
      <p className="muted" style={{ marginTop: -4 }}>
        Commission structures · <code style={{ fontSize: 11 }}>{data.user.email}</code>
      </p>

      <Panel title="Current structure">
        {openStructure ? (
          <Defs>
            <Def label="Type" value={openStructure.structure_type} />
            <Def label="Base rate" value={formatPercent(openStructure.base_commission_rate)} />
            {openStructure.structure_type === "split_equity" && (
              <>
                <Def
                  label="Equity / cash split"
                  value={
                    openStructure.equity_split_rate != null && openStructure.cash_split_rate != null
                      ? `${(openStructure.equity_split_rate * 100).toFixed(0)}% equity · ${(openStructure.cash_split_rate * 100).toFixed(0)}% cash`
                      : "—"
                  }
                />
                <Def
                  label="Annual equity cap"
                  value={
                    openStructure.equity_annual_cap != null
                      ? formatMoney(openStructure.equity_annual_cap, openStructure.cap_currency ?? "EUR")
                      : "—"
                  }
                />
              </>
            )}
            <Def label="Effective from" value={formatDate(openStructure.effective_from)} />
            {openStructure.notes && <Def label="Notes" value={openStructure.notes} />}
          </Defs>
        ) : (
          <EmptyState icon="generic" title="No active structure" body="This rep can't earn commissions until you create one below." tone="subtle" />
        )}
      </Panel>

      <Panel title="Create new structure">
        <p className="muted" style={{ fontSize: 12.5 }}>
          Leaving <em>effective_to</em> blank supersedes the current open structure (closes it the day before this one starts).
          Setting <em>effective_to</em> makes this a historical backfill row that coexists with the current open one.
        </p>
        <CreateStructureForm
          openGrant={openGrant}
          onSubmit={(payload) => createStructure.mutate(payload)}
          disabled={createStructure.isPending}
        />
      </Panel>

      <Panel title="History" padding="tight">
        {data.structures.length === 0 ? (
          <EmptyState icon="generic" title="No history yet" tone="subtle" />
        ) : (
          <DataTable>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Base</th>
                  <th>Equity split</th>
                  <th>Cap</th>
                  <th>Effective from</th>
                  <th>Effective to</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.structures.map((s) => (
                  <tr key={s.id} style={s.effective_to ? { opacity: 0.7 } : undefined}>
                    <td>{s.structure_type}</td>
                    <td>{formatPercent(s.base_commission_rate)}</td>
                    <td className="muted">
                      {s.equity_split_rate != null ? `${(s.equity_split_rate * 100).toFixed(0)}%` : "—"}
                    </td>
                    <td className="muted">
                      {s.equity_annual_cap != null
                        ? formatMoney(s.equity_annual_cap, s.cap_currency ?? "EUR")
                        : "—"}
                    </td>
                    <td className="muted">{formatDate(s.effective_from)}</td>
                    <td className="muted">{s.effective_to ? formatDate(s.effective_to) : <span className="badge earned">open</span>}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{s.notes ?? "—"}</td>
                    <td>
                      <button
                        style={{ color: "var(--err)", borderColor: "var(--err)", padding: "4px 10px", fontSize: 12 }}
                        disabled={deleteStructure.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Delete structure",
                            prompt: `Delete this structure row? This is irreversible — historical commissions that ran against it will NOT recompute.`,
                            tone: "destructive",
                            confirmLabel: "Delete",
                          });
                          if (ok) deleteStructure.mutate(s.id);
                        }}
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>

      <Panel title="Create equity grant">
        <p className="muted" style={{ fontSize: 12.5 }}>
          A new grant supersedes the currently-open one (closes it the day before the new <em>granted_on</em>).
          Linking a structure to this grant derives the equity cap from
          <code> annual_share_cap × share_value</code> — no separate cap input.
        </p>
        <CreateGrantForm onSubmit={(body) => createGrant.mutate(body)} disabled={createGrant.isPending} />
      </Panel>

      <Panel title="Equity grants" padding="tight">
        {data.grants.length === 0 ? (
          <EmptyState
            icon="generic"
            title="No equity grants yet"
            body="Create one above to wire up split-equity commission caps."
            tone="subtle"
          />
        ) : (
          <DataTable>
            <table>
              <thead>
                <tr>
                  <th>Granted</th>
                  <th>Superseded</th>
                  <th>Total shares</th>
                  <th>Annual cap</th>
                  <th>Share value</th>
                  <th>Vehicle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.grants.map((g) => (
                  <tr key={g.id} style={g.superseded_on ? { opacity: 0.7 } : undefined}>
                    <td className="muted">{formatDate(g.granted_on)}</td>
                    <td className="muted">{g.superseded_on ? formatDate(g.superseded_on) : <span className="badge earned">active</span>}</td>
                    <td>{g.total_shares.toLocaleString()}</td>
                    <td>{g.annual_share_cap.toLocaleString()}</td>
                    <td>{formatMoney(g.share_value_amount, g.share_value_currency)}</td>
                    <td className="muted">{g.vehicle ?? "—"}</td>
                    <td>
                      <button
                        style={{ color: "var(--err)", borderColor: "var(--err)", padding: "4px 10px", fontSize: 12 }}
                        disabled={deleteGrant.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Delete grant",
                            prompt: "Delete this grant row? Any commission_structures still linking to it will lose their derived cap.",
                            tone: "destructive",
                            confirmLabel: "Delete",
                          });
                          if (ok) deleteGrant.mutate(g.id);
                        }}
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>
    </>
  );
}

function CreateStructureForm({
  openGrant,
  onSubmit,
  disabled,
}: {
  openGrant: Grant | undefined;
  onSubmit: (payload: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const today = todayLocalDate();
  const [type, setType] = useState<"flat" | "split_equity">("flat");
  const [baseRate, setBaseRate] = useState("0.10");
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [effectiveTo, setEffectiveTo] = useState("");
  const [equitySplit, setEquitySplit] = useState("0.4");
  const [cashSplit, setCashSplit] = useState("0.6");
  const [capMajor, setCapMajor] = useState("");
  const [capCcy, setCapCcy] = useState("EUR");
  const [useGrant, setUseGrant] = useState(true);
  const [notes, setNotes] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {
      structure_type: type,
      base_commission_rate: Number(baseRate),
      effective_from: effectiveFrom,
      effective_to: effectiveTo || null,
      notes: notes.trim() || null,
    };
    if (type === "split_equity") {
      body.equity_split_rate = Number(equitySplit);
      body.cash_split_rate = Number(cashSplit);
      if (openGrant && useGrant) {
        body.equity_grant_id = openGrant.id;
      } else if (capMajor) {
        body.equity_annual_cap = Math.round(Number(capMajor) * 100);
        body.cap_currency = capCcy.toUpperCase();
      }
    }
    onSubmit(body);
    setNotes("");
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, alignItems: "end" }}>
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as "flat" | "split_equity")}>
            <option value="flat">flat</option>
            <option value="split_equity">split_equity</option>
          </select>
        </label>
        <label>
          Base rate (0..1)
          <input
            type="number"
            step="0.001"
            min="0"
            max="1"
            value={baseRate}
            onChange={(e) => setBaseRate(e.target.value)}
            required
          />
        </label>
        <label>
          Effective from
          <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
        </label>
        <label>
          Effective to (blank = current open)
          <input type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
        </label>
      </div>

      {type === "split_equity" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 8, alignItems: "end" }}>
          <label>
            Equity split rate
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={equitySplit}
              onChange={(e) => setEquitySplit(e.target.value)}
            />
          </label>
          <label>
            Cash split rate
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={cashSplit}
              onChange={(e) => setCashSplit(e.target.value)}
            />
          </label>
          {openGrant ? (
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
              <input type="checkbox" checked={useGrant} onChange={(e) => setUseGrant(e.target.checked)} />
              Derive cap from active grant ({openGrant.annual_share_cap.toLocaleString()} × {formatMoney(openGrant.share_value_amount, openGrant.share_value_currency)})
            </label>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <label style={{ flex: 1 }}>
                Annual cap (major)
                <input type="number" min="0" step="1" value={capMajor} onChange={(e) => setCapMajor(e.target.value)} />
              </label>
              <label style={{ width: 100 }}>
                Currency
                <input
                  maxLength={3}
                  value={capCcy}
                  onChange={(e) => setCapCcy(e.target.value)}
                  style={{ textTransform: "uppercase" }}
                />
              </label>
            </div>
          )}
        </div>
      )}

      <label>
        Notes (optional)
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 2026 H1 promotion" />
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="submit" className="primary" disabled={disabled}>
          {disabled ? "Saving…" : "Save structure"}
        </button>
      </div>
    </form>
  );
}

function CreateGrantForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (body: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const today = todayLocalDate();
  const [grantedOn, setGrantedOn] = useState(today);
  const [totalShares, setTotalShares] = useState("");
  const [annualCap, setAnnualCap] = useState("");
  const [shareValueMajor, setShareValueMajor] = useState("");
  const [shareValueCurrency, setShareValueCurrency] = useState("EUR");
  const [vehicle, setVehicle] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [horizon, setHorizon] = useState("4");
  const [rollover, setRollover] = useState(false);
  const [notes, setNotes] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      granted_on: grantedOn,
      total_shares: Number(totalShares),
      annual_share_cap: Number(annualCap),
      share_value_major: Number(shareValueMajor),
      share_value_currency: shareValueCurrency,
      vehicle: vehicle.trim() || null,
      entity_jurisdiction: jurisdiction.trim() || null,
      grant_horizon_years: Number(horizon),
      unearned_rollover: rollover,
      notes: notes.trim() || null,
    });
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, alignItems: "end" }}>
        <label>
          Granted on
          <input type="date" value={grantedOn} onChange={(e) => setGrantedOn(e.target.value)} required />
        </label>
        <label>
          Total shares
          <input
            type="number"
            min="1"
            step="1"
            placeholder="1000"
            value={totalShares}
            onChange={(e) => setTotalShares(e.target.value)}
            required
          />
        </label>
        <label>
          Annual share cap
          <input
            type="number"
            min="1"
            step="1"
            placeholder="250"
            value={annualCap}
            onChange={(e) => setAnnualCap(e.target.value)}
            required
          />
        </label>
        <label>
          Share value (major)
          <input
            type="number"
            min="0.01"
            step="0.01"
            placeholder="12.50"
            value={shareValueMajor}
            onChange={(e) => setShareValueMajor(e.target.value)}
            required
          />
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "0.7fr 1fr 1fr 0.7fr 1.4fr", gap: 8, alignItems: "end" }}>
        <label>
          Currency
          <input
            maxLength={3}
            value={shareValueCurrency}
            onChange={(e) => setShareValueCurrency(e.target.value.toUpperCase())}
            style={{ textTransform: "uppercase" }}
          />
        </label>
        <label>
          Vehicle (optional)
          <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="ISO, RSU, …" />
        </label>
        <label>
          Jurisdiction (optional)
          <input
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            maxLength={2}
            style={{ textTransform: "uppercase" }}
            placeholder="GB"
          />
        </label>
        <label>
          Horizon (years)
          <input type="number" min="1" max="20" step="1" value={horizon} onChange={(e) => setHorizon(e.target.value)} />
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
          <input type="checkbox" checked={rollover} onChange={(e) => setRollover(e.target.checked)} />
          <span>Unearned shares roll over year-to-year</span>
        </label>
      </div>
      <label>
        Notes (optional)
        <input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="submit" className="primary" disabled={disabled}>
          {disabled ? "Saving…" : "Save grant"}
        </button>
      </div>
    </form>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return (
    <table>
      <tbody>{children}</tbody>
    </table>
  );
}
function Def({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <th style={{ width: 180, textAlign: "left", fontWeight: 500, fontSize: 12, color: "var(--muted)" }}>{label}</th>
      <td>{value}</td>
    </tr>
  );
}
